/**
 * Wraps the provided function in a Promise and executes it asynchronously.
 *
 * @remarks
 * This utility accepts a parameterless function `fn` and schedules its execution using
 * a zero-delay `setTimeout` to ensure asynchronous behavior. The returned Promise resolves
 * with the result of `fn` if it completes successfully, or rejects with an error if one occurs.
 *
 * @example
 * const asyncFunction = () => "Hello, world!";
 * wrapAsync(asyncFunction).then(result => console.log(result)); // Logs "Hello, world!"
 *
 * @template T - A function type that takes no arguments and returns a value.
 * @param fn - The function whose execution is to be wrapped in a Promise.
 * @returns A Promise that resolves with the return value of `fn` if successful, or rejects with an error.
 */
export function wrapAsync<T>(fn: () => T) {
  return new Promise<T>((resolve, reject) => {
    setTimeout(() => {
      try {
        resolve(fn())
      } catch (error) {
        reject(error)
      }
    }, 0)
  })
}
